import type {
  CleanupFrequency,
  LastCleaned,
  LineItem,
  PricingRule,
  QuoteOutcome,
  ServiceZone,
  YardSize,
} from "@/lib/types";

export type QuoteInput = {
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  dog_count: number;
  yard_size: YardSize;
  frequency: CleanupFrequency;
  last_cleaned: LastCleaned;
  gate_access: string;
  dogs_remain_outside: boolean;
  trash_cart_available: boolean;
  desired_start_date: string;
  phone: string;
  email: string;
  lat?: number | null;
  lng?: number | null;
};

export type QuoteCalculation = {
  zone_id: string | null;
  zone_code: string | null;
  frequency: CleanupFrequency;
  line_items: LineItem[];
  recurring_total_cents: number;
  /**
   * When set, this is the **total** price for the first cleanup (not a surcharge
   * on top of the recurring visit). Recurring rate starts on the next visit.
   */
  initial_cleanup_cents: number | null;
  catch_up_included_minutes: number | null;
  rush_surcharge_cents: number | null;
  outcome: QuoteOutcome;
  service_day: number | null;
  message: string;
};

const FREQ_BASE_KEY: Record<CleanupFrequency, string> = {
  twice_weekly: "twice_weekly_base",
  weekly: "weekly_base",
  biweekly: "biweekly_base",
};

const FREQ_DEFAULT: Record<CleanupFrequency, number> = {
  twice_weekly: 1799,
  weekly: 1999,
  biweekly: 2999,
};

const FREQ_NOTE: Record<CleanupFrequency, string> = {
  weekly: "Per completed visit for weekly service.",
  biweekly: "Per completed visit for every-other-week service.",
  twice_weekly: "Per completed visit for twice-weekly service.",
};

const LABELS: Record<string, string> = {
  weekly_base: "Weekly (per visit)",
  twice_weekly_base: "Twice weekly (per visit)",
  biweekly_base: "Every other week (per visit)",
  additional_dog: "Additional dog",
  large_yard: "Larger-than-average yard",
  extra_large_yard: "Very large yard",
  outside_core_zone: "Location adjustment",
  haul_away: "Haul-away (no trash cart)",
  catch_up_cleanup: "First cleanup total",
  same_day: "Rush scheduling surcharge (from)",
};

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** South Goodyear / Estrella Mountain Ranch approximate box (not Laveen 85339). */
const ESTRELLA_BOUNDS = {
  minLat: 33.27,
  maxLat: 33.405,
  minLng: -112.48,
  maxLng: -112.3,
};

function ruleMap(rules: PricingRule[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const r of rules) {
    if (r.active) m[r.key] = r.amount_cents;
  }
  return m;
}

function looksLikeEstrella(
  addressLine1: string,
  lat?: number | null,
  lng?: number | null,
): boolean {
  const a = addressLine1.toUpperCase();
  if (a.includes("ESTRELLA")) return true;
  if (lat == null || lng == null) return false;
  return (
    lat >= ESTRELLA_BOUNDS.minLat &&
    lat <= ESTRELLA_BOUNDS.maxLat &&
    lng >= ESTRELLA_BOUNDS.minLng &&
    lng <= ESTRELLA_BOUNDS.maxLng
  );
}

export function findZone(
  postalCode: string,
  zones: ServiceZone[],
  opts?: {
    address_line1?: string;
    lat?: number | null;
    lng?: number | null;
  },
): ServiceZone | null {
  const zip = postalCode.trim().slice(0, 5);
  // 85339 is Laveen — not Estrella / Goodyear.
  if (zip === "85339") return null;

  const matches = zones.filter((z) => z.zip_codes.includes(zip));
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];

  const estrella = matches.find((z) => z.code === "GY-2");
  const central = matches.find((z) => z.code === "GY-1") ?? matches[0];
  if (
    estrella &&
    looksLikeEstrella(opts?.address_line1 ?? "", opts?.lat, opts?.lng)
  ) {
    return estrella;
  }
  return central;
}

function deriveZoneStatus(zone: ServiceZone): ServiceZone["status"] {
  // Owner/ops can force open/limited/full/closed; only auto-derive while waitlisted.
  if (zone.status !== "waitlist") return zone.status;
  const n = zone.current_customer_count;
  if (n >= zone.full_threshold) return "full";
  if (n >= zone.open_threshold) return "open";
  if (n >= zone.limited_threshold) return "limited";
  return "waitlist";
}

/** Catch-up when last cleaned > 14 days, unknown, or never. Amounts are first-cleanup totals. */
export function catchUpForLastCleaned(
  last: LastCleaned,
  amounts: Record<string, number>,
): { cents: number; includedMinutes: number } | null {
  switch (last) {
    case "within_week":
    case "1_2_weeks":
      return null;
    case "15_30_days":
      return {
        cents: amounts.catch_up_15_30 ?? amounts.initial_cleanup_min ?? 4900,
        includedMinutes: 30,
      };
    case "1_2_months":
      return {
        cents: amounts.catch_up_31_60 ?? 6900,
        includedMinutes: 45,
      };
    case "1_month_plus":
    case "never":
    case "unknown":
      return {
        cents: amounts.catch_up_61_plus ?? 8900,
        includedMinutes: 60,
      };
    default:
      return null;
  }
}

export function calculateQuote(
  input: QuoteInput,
  zones: ServiceZone[],
  rules: PricingRule[],
  todayIso = new Date().toISOString().slice(0, 10),
): QuoteCalculation {
  const amounts = ruleMap(rules);
  const zone = findZone(input.postal_code, zones, {
    address_line1: input.address_line1,
    lat: input.lat,
    lng: input.lng,
  });
  const line_items: LineItem[] = [];

  const push = (key: string, amount: number, label?: string) => {
    if (amount <= 0) return;
    line_items.push({
      key,
      label: label ?? LABELS[key] ?? key,
      amount_cents: amount,
    });
  };

  if (!zone) {
    return {
      zone_id: null,
      zone_code: null,
      frequency: input.frequency,
      line_items: [],
      recurring_total_cents: 0,
      initial_cleanup_cents: null,
      catch_up_included_minutes: null,
      rush_surcharge_cents: null,
      outcome: "outside_area",
      service_day: null,
      message:
        "We are not servicing your neighborhood yet. Join the waitlist and we'll contact you as the route expands.",
    };
  }

  const effectiveStatus = deriveZoneStatus(zone);
  const baseKey = FREQ_BASE_KEY[input.frequency];
  const baseAmount =
    amounts[baseKey] ??
    (input.frequency === "weekly" ? amounts.weekly_base : undefined) ??
    FREQ_DEFAULT[input.frequency];
  push(baseKey, baseAmount);

  const extraDogs = Math.max(0, input.dog_count - 1);
  push(
    "additional_dog",
    extraDogs * (amounts.additional_dog ?? 400),
    extraDogs > 1 ? `Additional dogs (×${extraDogs})` : undefined,
  );

  if (input.yard_size === "large") {
    push("large_yard", amounts.large_yard ?? 1000);
  }
  if (input.yard_size === "extra_large") {
    push("extra_large_yard", amounts.extra_large_yard ?? 2000);
  }

  const surcharge = zone.surcharge_cents > 0 ? zone.surcharge_cents : 0;
  if (surcharge > 0) {
    push("outside_core_zone", surcharge, "Location adjustment");
  }

  // Recurring visits: cart disposal included; haul-away is +$5 when no cart.
  // First/catch-up and one-time cleanups include haul-away in their totals.
  if (!input.trash_cart_available) {
    push("haul_away", amounts.haul_away ?? 500);
  }

  const recurring_total_cents = line_items.reduce(
    (s, i) => s + i.amount_cents,
    0,
  );

  const catchUp = catchUpForLastCleaned(input.last_cleaned, amounts);
  const initial_cleanup_cents = catchUp?.cents ?? null;
  const catch_up_included_minutes = catchUp?.includedMinutes ?? null;

  const sameDay = input.desired_start_date === todayIso;
  const rush_surcharge_cents = sameDay
    ? amounts.same_day ?? amounts.rush_from ?? 3900
    : null;

  const needsInspection =
    input.yard_size === "oversized" ||
    catchUp != null ||
    input.dog_count >= 5;

  let outcome: QuoteOutcome;
  if (
    effectiveStatus === "closed" ||
    effectiveStatus === "waitlist" ||
    effectiveStatus === "full"
  ) {
    outcome = "waitlisted";
  } else if (input.yard_size === "oversized") {
    outcome = "needs_inspection";
  } else if (needsInspection && catchUp != null) {
    outcome = "needs_inspection";
  } else if (surcharge > 0) {
    outcome = "zone_surcharge";
  } else {
    outcome = "instant_signup";
  }

  const dayName = DAY_NAMES[zone.default_service_day] ?? "your assigned day";

  const messages: Record<QuoteOutcome, string> = {
    outside_area:
      "We are not servicing your neighborhood yet. Join the waitlist and we'll contact you as the route expands.",
    waitlisted:
      "We are not servicing your neighborhood yet. Join the waitlist and we'll contact you as the route expands.",
    needs_inspection:
      input.yard_size === "oversized"
        ? "Oversized or multi-area properties need a custom quote before service begins."
        : catchUp
          ? `First cleanup total ${formatCents(catchUp.cents)} (up to ${catchUp.includedMinutes} minutes). Your regular recurring rate begins with the following scheduled visit. Extra time only with your approval.`
          : "An on-site review is recommended before recurring service begins.",
    zone_surcharge: `We service your address. A ${formatCents(surcharge)} location adjustment is included in your displayed price. Your normal service day is ${dayName}.`,
    instant_signup: `Great news—we service your address. Your normal service day is ${dayName}.`,
  };

  return {
    zone_id: zone.id,
    zone_code: zone.code,
    frequency: input.frequency,
    line_items,
    recurring_total_cents,
    initial_cleanup_cents,
    catch_up_included_minutes,
    rush_surcharge_cents,
    outcome,
    service_day: zone.default_service_day,
    message: `${messages[outcome]} ${FREQ_NOTE[input.frequency]}`,
  };
}

export function isSignupEligible(outcome: QuoteOutcome): boolean {
  return (
    outcome === "instant_signup" ||
    outcome === "zone_surcharge" ||
    outcome === "needs_inspection"
  );
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export type FinalizedVisitPrice = {
  line_items: LineItem[];
  recurring_total_cents: number;
  /** First completed visit total (catch-up if any, else recurring). */
  first_visit_total_cents: number;
  initial_cleanup_cents: number | null;
  haul_away_cents: number;
  trash_cart_available: boolean;
};

/**
 * Single source of truth for visit pricing at signup / account changes.
 * Recalculates recurring total from disposal choice (haul-away when no cart).
 */
export function finalizeVisitPrice(opts: {
  frequency: CleanupFrequency;
  dog_count: number;
  yard_size: YardSize;
  trash_cart_available: boolean;
  zone_surcharge_cents?: number;
  /** When set, first visit uses this catch-up total instead of recurring. */
  initial_cleanup_cents?: number | null;
  rules: PricingRule[];
}): FinalizedVisitPrice {
  const amounts = ruleMap(opts.rules);
  const line_items: LineItem[] = [];
  const push = (key: string, amount: number, label?: string) => {
    if (amount <= 0) return;
    line_items.push({
      key,
      label: label ?? LABELS[key] ?? key,
      amount_cents: amount,
    });
  };

  const baseKey = FREQ_BASE_KEY[opts.frequency];
  const baseAmount =
    amounts[baseKey] ??
    (opts.frequency === "weekly" ? amounts.weekly_base : undefined) ??
    FREQ_DEFAULT[opts.frequency];
  push(baseKey, baseAmount);

  const extraDogs = Math.max(0, opts.dog_count - 1);
  push(
    "additional_dog",
    extraDogs * (amounts.additional_dog ?? 400),
    extraDogs > 1 ? `Additional dogs (×${extraDogs})` : undefined,
  );

  if (opts.yard_size === "large") {
    push("large_yard", amounts.large_yard ?? 1000);
  }
  if (opts.yard_size === "extra_large") {
    push("extra_large_yard", amounts.extra_large_yard ?? 2000);
  }

  const surcharge = opts.zone_surcharge_cents ?? 0;
  if (surcharge > 0) {
    push("outside_core_zone", surcharge, "Location adjustment");
  }

  const haul_away_cents = opts.trash_cart_available
    ? 0
    : amounts.haul_away ?? 500;
  if (haul_away_cents > 0) {
    push("haul_away", haul_away_cents, "Waste haul-away");
  }

  const recurring_total_cents = line_items.reduce(
    (s, i) => s + i.amount_cents,
    0,
  );
  const initial_cleanup_cents = opts.initial_cleanup_cents ?? null;
  const first_visit_total_cents =
    initial_cleanup_cents != null
      ? initial_cleanup_cents
      : recurring_total_cents;

  return {
    line_items,
    recurring_total_cents,
    first_visit_total_cents,
    initial_cleanup_cents,
    haul_away_cents,
    trash_cart_available: opts.trash_cart_available,
  };
}
